/**
 * geotracker-bisawofa.js — BisaWofa integration layer for the reusable
 * GeoTracker engine.
 *
 * This is the ONLY place that couples the generic src/geotracker/ engine to
 * BisaWofa specifics (SQLocal storage). To reuse GeoTracker in another app,
 * copy src/geotracker/ and write a file like this one with that app's storage
 * (and optional sync) adapters.
 *
 * Storage: SQLocal (local-only). Recorded trails persist in the local
 * gps_trails / gps_trail_points tables and survive reloads. Server sync is
 * intentionally NOT wired yet — the BisaWofa trail endpoints don't exist. When
 * they do, add a `sync` adapter (pushTrail / isOnline) here with zero changes
 * to the engine, and trails recorded offline will store-and-forward
 * automatically.
 *
 * @module geotracker-bisawofa
 */

import { GeoTracker } from './geotracker/GeoTracker.js';
import {
  createGpsTrail,
  addGpsTrailPoint,
  finishGpsTrail,
  getUnsyncedGpsTrails,
  getGpsTrailPoints,
  markGpsTrailSynced,
} from './database.js';

/**
 * Storage adapter — maps the GeoTracker contract onto the SQLocal helpers in
 * database.js.
 */
const sqlocalStorage = {
  createTrail:       (meta)            => createGpsTrail(meta),
  addPoint:          (trailId, point)  => addGpsTrailPoint(trailId, point),
  finishTrail:       (trailId, summ)   => finishGpsTrail(trailId, summ),
  getUnsyncedTrails: ()                => getUnsyncedGpsTrails(),
  getTrailPoints:    (trailId)         => getGpsTrailPoints(trailId),
  markTrailSynced:   (trailId, remote) => markGpsTrailSynced(trailId, remote),
};

/**
 * The configured, app-wide tracker instance. Tunables chosen for field
 * walking/driving across Africa: a point every ~5 m, throttled to ≥1 s, with
 * a 20 s heartbeat so stationary pauses still leave a breadcrumb, dropping
 * fixes worse than 50 m accuracy.
 *
 * No `sync` adapter is configured → recording is local-only for now. The
 * engine handles a missing sync adapter gracefully (stopRecording skips the
 * push, syncPending is a no-op).
 */
export const geoTracker = new GeoTracker({
  storage: sqlocalStorage,
  minDistanceM: 5,
  minIntervalMs: 1000,
  heartbeatMs: 20000,
  maxAccuracyM: 50,
  enableHighAccuracy: true,
});

export default geoTracker;
